import {
  AssetStructListAction,
  KalturaAssetStruct,
  KalturaLongValue,
  KalturaMeta,
  KalturaObjectVirtualAssetInfo,
  KalturaObjectVirtualAssetInfoType,
  KalturaObjectVirtualAssetPartnerConfig,
  KalturaPartnerConfigurationFilter,
  KalturaPartnerConfigurationType,
  MetaListAction,
  PartnerConfigurationListAction,
  PartnerConfigurationUpdateAction,
} from '@kaltura-ott/ott-client-types/api/types';
import { KalturaAssetStructListResponse } from '@kaltura-ott/ott-client-types/api/types/KalturaAssetStructListResponse';
import { KalturaMetaListResponse } from '@kaltura-ott/ott-client-types/api/types/KalturaMetaListResponse';
import { KalturaPartnerConfigurationListResponse } from '@kaltura-ott/ott-client-types/api/types/KalturaPartnerConfigurationListResponse';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { kalturaClient } from '../kaltura-clent';
import { CATEGORY_ID_SYSTEM_NAME, MAIN_VIRTUAL_ASSET_STRUCT_NAME } from '../utils/constants';

export const getMainVirtualStruct = (): Observable<KalturaAssetStruct | undefined> => {
  return kalturaClient()
    .request(new AssetStructListAction())
    .pipe(
      map((resp: KalturaAssetStructListResponse) => {
        if (resp.objects) {
          return resp.objects.find((struct) => struct.systemName === MAIN_VIRTUAL_ASSET_STRUCT_NAME);
        }
        return;
      }),
    );
};

export const getMainVirtualMeta = (): Observable<KalturaMeta | undefined> => {
  return kalturaClient()
    .request(new MetaListAction())
    .pipe(
      map((resp: KalturaMetaListResponse) => {
        if (resp.objects) {
          return resp.objects.find((struct) => struct.systemName === CATEGORY_ID_SYSTEM_NAME);
        }
        return;
      }),
    );
};

export const extractPartnerVirtualAssetInfoFromConfig = (
  config: KalturaPartnerConfigurationListResponse,
): KalturaObjectVirtualAssetInfo | undefined => {
  if (config.objects) {
    const virtualAssetsConfig = config.objects[0] as KalturaObjectVirtualAssetPartnerConfig;
    if (virtualAssetsConfig && virtualAssetsConfig.objectVirtualAssets) {
      return virtualAssetsConfig.objectVirtualAssets.find(
        (virtualAsset) => virtualAsset.type === KalturaObjectVirtualAssetInfoType.category,
      );
    }
  }
  return;
};

/**
 * Will get partner virtual asset configuration
 */
export const getCategoryVirtualAssetConfig = (): Observable<KalturaObjectVirtualAssetInfo> => {
  const client = kalturaClient();
  const action = new PartnerConfigurationListAction({
    filter: new KalturaPartnerConfigurationFilter({
      partnerConfigurationTypeEqual: KalturaPartnerConfigurationType.objectvirtualasset,
    }),
  });
  return client.request(action).pipe(
    map((resp: KalturaPartnerConfigurationListResponse) => {
      return extractPartnerVirtualAssetInfoFromConfig(resp) as KalturaObjectVirtualAssetInfo;
    }),
    catchError((error) => {
      return of(error);
    }),
  );
};

/**
 * Will init partner virtual asset configuration
 */
export const initCategoryVirtualAssetConfig = (): Observable<boolean> => {
  return forkJoin([getMainVirtualStruct(), getMainVirtualMeta()]).pipe(
    mergeMap((resp) => {
      const mainVirtualAssetStruct = resp[0];
      const mainVirtualAssetMeta = resp[1];
      return kalturaClient().request(
        new PartnerConfigurationUpdateAction({
          configuration: new KalturaObjectVirtualAssetPartnerConfig({
            objectVirtualAssets: [
              new KalturaObjectVirtualAssetInfo({
                // @ts-expect-error TODO Fix
                assetStructId: mainVirtualAssetStruct.id,
                // @ts-expect-error TODO Fix
                metaId: parseInt(mainVirtualAssetMeta.id, 10),
                type: KalturaObjectVirtualAssetInfoType.category,
              }),
            ],
          }),
        }),
      );
    }),
    map((resp) => {
      return resp;
    }),
    catchError((error) => {
      return of(error);
    }),
  );
};

/**
 * Will add external extended virtual asset type
 *
 * @param structs
 */
export const addExtendedVirtualTypes = (structs: Array<KalturaAssetStruct>): Observable<boolean> => {
  return getCategoryVirtualAssetConfig().pipe(
    map((resp: KalturaObjectVirtualAssetInfo) => {
      if (resp) return resp;
      return new KalturaObjectVirtualAssetInfo({ extendedTypes: {} });
    }),
    mergeMap((virtualAssetInfo: KalturaObjectVirtualAssetInfo) => {
      structs.forEach((struct) => {
        virtualAssetInfo.extendedTypes[struct.systemName] = new KalturaLongValue({ value: struct.id });
      });
      return kalturaClient().request(
        new PartnerConfigurationUpdateAction({
          configuration: new KalturaObjectVirtualAssetPartnerConfig({
            objectVirtualAssets: [virtualAssetInfo],
          }),
        }),
      );
    }),
    map((resp) => {
      return resp;
    }),
    catchError((error) => {
      return of(error);
    }),
  );
};
